import { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Play, Pause } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { VoucherRequiredError } from '@/lib/api';

type TrackSuggestion = Awaited<ReturnType<typeof api.ai.suggestMusic>>['tracks'][number];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberId: string;
  memberName: string;
  onPick: (trackId: string) => void;
  onVoucherRequired: () => void;
}

export function MusicSuggestDialog({ open, onOpenChange, memberId, memberName, onPick, onVoucherRequired }: Props) {
  const [tracks, setTracks] = useState<TrackSuggestion[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!open) {
      setTracks(null);
      setPlayingId(null);
      audioRef.current?.pause();
      audioRef.current = null;
      return;
    }
    setLoading(true);
    api.ai.suggestMusic(memberId)
      .then((res) => setTracks(res.tracks))
      .catch((err) => {
        if (err instanceof VoucherRequiredError) {
          onOpenChange(false);
          onVoucherRequired();
          return;
        }
        toast.error(err instanceof Error ? err.message : 'Fehler bei der Song-Suche');
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, memberId, onOpenChange, onVoucherRequired]);

  function togglePreview(track: TrackSuggestion) {
    if (!track.previewUrl) return;
    if (playingId === track.id) {
      audioRef.current?.pause();
      audioRef.current = null;
      setPlayingId(null);
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(track.previewUrl);
    audio.onended = () => setPlayingId(null);
    audio.play().catch(() => setPlayingId(null));
    audioRef.current = audio;
    setPlayingId(track.id);
  }

  function handlePick(track: TrackSuggestion) {
    audioRef.current?.pause();
    audioRef.current = null;
    onPick(track.id);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85dvh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>Musik für {memberName}</DialogTitle>
          <DialogDescription>
            Ein paar Vorschläge, die emotional zu diesem Anteil passen könnten. Tippe auf den Play-Button, um 30 Sekunden zu hören.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground gap-2">
            <Loader2 size={16} className="animate-spin" />
            Suche passende Songs…
          </div>
        )}

        {!loading && tracks && tracks.length === 0 && (
          <p className="text-sm text-muted-foreground py-4">Keine Vorschläge gefunden. Versuch es nochmal.</p>
        )}

        {!loading && tracks && tracks.length > 0 && (
          <div className="space-y-2 mt-2">
            {tracks.map((t) => {
              const isPlaying = playingId === t.id;
              return (
                <div key={t.id} className="rounded-xl border border-border p-3 space-y-2">
                  <div className="flex items-start gap-3">
                    {t.albumImageUrl ? (
                      <img
                        src={t.albumImageUrl}
                        alt=""
                        className="w-14 h-14 rounded-lg object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-muted shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium break-words">{t.name}</p>
                      <p className="text-xs text-muted-foreground break-words">{t.artists.join(', ')}</p>
                      <p className="text-xs text-muted-foreground italic mt-1 break-words">{t.reason}</p>
                    </div>
                    {t.previewUrl && (
                      <button
                        type="button"
                        onClick={() => togglePreview(t)}
                        className="w-9 h-9 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center shrink-0"
                        aria-label={isPlaying ? 'Preview stoppen' : 'Preview abspielen'}
                      >
                        {isPlaying ? <Pause size={14} /> : <Play size={14} className="translate-x-[1px]" />}
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2 justify-end">
                    <a
                      href={t.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-muted-foreground underline underline-offset-2 self-center mr-auto"
                    >
                      Auf Spotify öffnen
                    </a>
                    <Button size="sm" onClick={() => handlePick(t)}>
                      Diesen wählen
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
